import {
    useMemo,
    useState,
} from 'react'

import Button from 'app/shared-components/Button'
import ButtonContainer from 'app/shared-components/ButtonContainer'
import useGetServiceCenterByLocation
    from 'app/Apps/ContainerManagement/ServiceCenters/hooks/useGetServiceCenterByLocation'
import {
    PositioningSelection,
} from 'app/enums/label.enums'

import useOrderManagementRoutes from 'app/SkyNetRoutes/hooks/useOrderManagementRoutes'
import {
    CancelOrderData,
    Order,
} from '../../orders.types'
import useCancelOrder from '../../hooks/useCancelOrder'
import CancellationOrderDialog from '../../Components/CancellationOrderDialog'
import PostponeDialog from '../CustomerTransportsOverview/PostponeDialog'
import useUpdateStatusOrder from '../hooks/useUpdateStatusOrder'

import LocationSelection from './LocationSelection'
import getServiceCenterMap from './getServiceCenterMap'
import PositioningSetup from './PositioningSetup/PositioningSetup'
import useUpdateOriginServiceCenter from './hooks/useUpdateOriginServiceCenter'
import {
    LocationSelectionDataType,
    PositioningSetupType,
} from './orderConfirmation.types'
import ContainerAvailabilityForecast from './ContainerAvailabilityForecast'

const OrderConfirmation = ({
    positioningSetup,
    order,
    isDeliveryRoad,
    onSuccess,
    invalidateOrder,
}: {
    positioningSetup: PositioningSetupType,
    order: Order,
    isDeliveryRoad: boolean,
    onSuccess: () => void
    invalidateOrder: () => void
}) => {
    const {
        CustomerOrders: CustomerOrdersRoutes,
    } = useOrderManagementRoutes()
    const {
        openCancellation,
        setCancellationOpen,
    }: CancelOrderData = useCancelOrder({
        orderStatus: order?.orderStatus,
    })

    const serviceCenterMap = useMemo(() => {
        return getServiceCenterMap(positioningSetup)
    }, [positioningSetup])

    const [
        locationSelectionData,
        setLocationSelectionData,
    ] = useState<LocationSelectionDataType>({
        ...serviceCenterMap.origin[positioningSetup.originServiceCenterSelection
        || PositioningSelection.DEFAULT].data,
        originServiceCenterSelection: positioningSetup.originServiceCenterSelection
            || PositioningSelection.DEFAULT,
    } as LocationSelectionDataType)

    const {
        data: serviceCenter,
    } = useGetServiceCenterByLocation(locationSelectionData.originServiceCenter?.id)

    const [
        openPostpone,
        setPostponeOpen,
    ] = useState<boolean>(false)

    const {
        mutate: updateOriginServiceCenter,
        isPending: isLoadingSaveLocationSelection,
    } = useUpdateOriginServiceCenter(onSuccess)

    const saveLocationSelection = () => {
        updateOriginServiceCenter({
            data: locationSelectionData,
            version: positioningSetup?.version,
            orderNumber: order?.orderNumber,
        })
    }

    // check if data was changed, if default selection is other,
    // location or contacts can be changed without changing selection

    const isSaveLocationEnabled = locationSelectionData.originServiceCenterSelection
        !== positioningSetup.originServiceCenterSelection
        || (positioningSetup.originServiceCenterSelection === PositioningSelection.OTHER
            && (locationSelectionData.originServiceCenter !== positioningSetup.originScOther
                || locationSelectionData.originServiceCenterContacts
                !== positioningSetup.originScOtherContacts))

    const onSuccessOrderChangeStatus = () => {
        CustomerOrdersRoutes.Overview.navigate({
            params: {
                key: order?.orderNumber,
            },
        })
    }

    const {
        mutate,
    } = useUpdateStatusOrder(onSuccessOrderChangeStatus)

    const confirmOrder = () => {
        mutate(order?.orderNumber)
    }

    return (
        <>
            <PositioningSetup
                data={positioningSetup}
                order={order}
                isDeliveryRoad={isDeliveryRoad}
                onSuccess={onSuccess}
            />
            <LocationSelection
                positioningSetup={positioningSetup}
                locationSelectionData={locationSelectionData}
                setLocationSelectionData={setLocationSelectionData}
                isDeliveryRoad={isDeliveryRoad}
                serviceCenter={serviceCenter}
            />
            <ContainerAvailabilityForecast
                serviceCenterId={serviceCenter?.id}
                requiredContainers={order?.requiredContainers}
                orderNumber={order?.orderNumber}
                requestedHandoverDate={order?.requestedHandover}
            />
            <ButtonContainer>
                {isDeliveryRoad && (
                    <Button
                        label="Save Location Selection"
                        onClick={saveLocationSelection}
                        data-testid="save-btn-selection"
                        disabled={!isSaveLocationEnabled}
                        saving={isLoadingSaveLocationSelection}
                    />
                )}
                <Button
                    label="confirm order"
                    onClick={confirmOrder}
                    disabled={isSaveLocationEnabled}
                />
                <Button
                    secondary
                    label="cancel order"
                    onClick={() => {
                        setCancellationOpen(true)
                    }}
                />
                <Button
                    data-testid="postpone-order-btn"
                    secondary
                    label="postpone order"
                    onClick={() => {
                        setPostponeOpen(true)
                    }}
                />
            </ButtonContainer>
            <CancellationOrderDialog
                orderNumber={order?.orderNumber}
                onSuccess={onSuccessOrderChangeStatus}
                open={openCancellation}
                setOpen={setCancellationOpen}
            />
            <PostponeDialog
                open={openPostpone}
                setOpen={setPostponeOpen}
                onSuccess={invalidateOrder}
                order={order}
            />
        </>
    )
}

export default OrderConfirmation
